import { moduleEnabled } from '@/lib/modules'
import type { TranslationKey } from '@/i18n/dictionary'
import type { User } from '@/types/api'

/*
 * The search palette's model: what a query finds, where each hit leads, and
 * the order the groups come in.
 *
 * Kept out of the component so the rules are tested as plain functions
 * (searchModel.test.ts). Two sources are merged here: the pages and actions
 * the client already knows about, which answer instantly and offline, and
 * `GET /api/search`, which answers for reports, machines and people a moment
 * later. The palette must be useful on the first keystroke and on a server
 * that has not shipped the endpoint yet — so the client half never waits for
 * the server half.
 */

export type PaletteKind = 'page' | 'action' | 'submission' | 'workCenter' | 'person' | 'more'

export interface PaletteItem {
  /** Unique across every group; the listbox option id is built from it. */
  id: string
  kind: PaletteKind
  label: string
  /** The second line: where it lives, its status, its role. */
  hint?: string
  /** A reference number or a code, set in monospace before the label. */
  badge?: string
  path: string
  /** Extra words that should find this — synonyms, the old name of a page. */
  keywords?: string
}

export type PaletteGroupId =
  | 'recent'
  | 'actions'
  | 'pages'
  | 'submissions'
  | 'workCenters'
  | 'people'

export interface PaletteGroup {
  id: PaletteGroupId
  labelKey: TranslationKey
  items: PaletteItem[]
}

// ---------------------------------------------------------------------------
// The server's answer (contract §2)
// ---------------------------------------------------------------------------

export interface SearchSubmission {
  id: string
  referenceNo: string
  title: string
  status: string
  statusLabel: string
  workCenter: { id: string; name: string } | null
  createdAt: string
}

export interface SearchWorkCenter {
  id: string
  code: string
  name: string
  plant: { id: string; name: string } | null
}

export interface SearchPerson {
  id: string
  name: string
  roleLabel: string
  employeeCode: string | null
}

export interface SearchResponse {
  data: {
    submissions: SearchSubmission[]
    workCenters: SearchWorkCenter[]
    people: SearchPerson[]
  }
  meta: {
    q: string
    counts: { submissions: number; workCenters: number; people: number }
  }
}

/** The server refuses shorter queries, so the client does not ask. */
export const MIN_SERVER_QUERY = 2

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * Lower case, without diacritics.
 *
 * A phone keyboard in a pogon is as often as not set to English, so "šifra"
 * and "sifra" have to be the same word, and so do "Juče" and "juce". `đ` does
 * not decompose under NFD — it is its own letter, not a d with a mark — so it
 * is mapped by hand.
 */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .trim()
}

/**
 * How well a query matches a page or an action. Zero is no match.
 *
 * Every word of the query has to appear somewhere, so "nova pr" finds "Nova
 * prijava" and "pr nova" does too. Then the label beats everything else: a
 * label that starts with the query is the page the person was typing the name
 * of, a word in the label that starts with it is nearly as good, and a match
 * found only in the hint or the keywords ranks last.
 */
export function matchScore(query: string, item: PaletteItem): number {
  const q = normalize(query)

  if (!q) return 0

  const label = normalize(item.label)
  const rest = normalize(`${item.hint ?? ''} ${item.keywords ?? ''}`)
  const words = q.split(/\s+/)

  if (!words.every((word) => label.includes(word) || rest.includes(word))) return 0

  if (label.startsWith(q)) return 100
  if (label.split(/\s+/).some((word) => word.startsWith(words[0]))) return 60
  if (words.every((word) => label.includes(word))) return 30

  return 10
}

/** The best static matches, best first; ties keep the navigation's order. */
export function matchStatic(query: string, items: PaletteItem[], limit = 6): PaletteItem[] {
  return items
    .map((item, index) => ({ item, index, score: matchScore(query, item) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map((entry) => entry.item)
}

// ---------------------------------------------------------------------------
// Where a hit leads
// ---------------------------------------------------------------------------

const isAdmin = (user: User | null) => user?.role === 'ADMIN'

/**
 * A machine has no screen of its own, so it opens onto the nearest thing that
 * is about it.
 *
 * An administrator searching for a machine is almost always about to change
 * it, so they get its row on the admin list. Everybody else gets the reports
 * filed against it, which is what the machine's history is. With reports
 * switched off there is nothing about one machine to show, and no hit is
 * better than a link to a page that does not mention it.
 */
export function workCenterPath(center: SearchWorkCenter, user: User | null): string | null {
  if (isAdmin(user)) return `/admin/radni-centri?search=${encodeURIComponent(center.code || center.name)}`

  if (moduleEnabled(user?.company?.modules, 'submissions')) {
    return `/prijave?workCenterId=${encodeURIComponent(center.id)}`
  }

  return null
}

/**
 * A person opens onto the screen that is about them, for whoever may see it.
 *
 * Administrators get their row on Korisnici, where the questions about a
 * person are answered — their role, their plants, their badge. A manager gets
 * the worker table on Analitika, filtered to them, which is the only place a
 * manager sees somebody's record. The server sends nobody else any people.
 */
export function personPath(person: SearchPerson, user: User | null): string | null {
  if (isAdmin(user)) return `/admin/korisnici?search=${encodeURIComponent(person.name)}`

  if (user?.isManager && moduleEnabled(user.company?.modules, 'analytics')) {
    return `/analitika?osoba=${encodeURIComponent(person.name)}#radnici`
  }

  return null
}

/** The list a "show every match" row opens, already filtered. */
function morePath(kind: 'submissions' | 'workCenters' | 'people', q: string, user: User | null) {
  const query = encodeURIComponent(q)

  if (kind === 'submissions') return `/prijave?search=${query}`
  if (!isAdmin(user)) return null

  return kind === 'people' ? `/admin/korisnici?search=${query}` : `/admin/radni-centri?search=${query}`
}

export interface ServerGroups {
  submissions: PaletteItem[]
  workCenters: PaletteItem[]
  people: PaletteItem[]
  /** Whether any hit is the exact reference or code that was typed. */
  exact: PaletteGroupId | null
}

/**
 * The server's answer as palette rows, plus a "show all" row where the server
 * says it found more than it sent and there is a list that can show them.
 *
 * `labels` carries the two sentences the rows need, already translated, so
 * this stays a plain function.
 */
export function serverGroups(
  response: SearchResponse | undefined,
  user: User | null,
  labels: { more: (count: string) => string },
): ServerGroups {
  const empty: ServerGroups = { submissions: [], workCenters: [], people: [], exact: null }

  if (!response) return empty

  const { data, meta } = response
  const q = normalize(meta.q)

  const submissions: PaletteItem[] = data.submissions.map((submission) => ({
    id: `submission:${submission.id}`,
    kind: 'submission',
    label: submission.title,
    badge: submission.referenceNo,
    hint: [submission.statusLabel, submission.workCenter?.name].filter(Boolean).join(' · '),
    path: `/prijave/${encodeURIComponent(submission.id)}`,
  }))

  const workCenters: PaletteItem[] = data.workCenters.flatMap((center) => {
    const path = workCenterPath(center, user)

    return path
      ? [
          {
            id: `workCenter:${center.id}`,
            kind: 'workCenter' as const,
            label: center.name,
            badge: center.code,
            hint: center.plant?.name,
            path,
          },
        ]
      : []
  })

  const people: PaletteItem[] = data.people.flatMap((person) => {
    const path = personPath(person, user)

    return path
      ? [
          {
            id: `person:${person.id}`,
            kind: 'person' as const,
            label: person.name,
            badge: person.employeeCode ?? undefined,
            hint: person.roleLabel,
            path,
          },
        ]
      : []
  })

  const withMore = (
    kind: 'submissions' | 'workCenters' | 'people',
    items: PaletteItem[],
  ): PaletteItem[] => {
    const total = meta.counts[kind]
    const path = morePath(kind, meta.q, user)

    if (!path || items.length === 0 || total <= items.length) return items

    // The server caps its counts at 100, so 100 means "at least".
    const count = total >= 100 ? '100+' : String(total)

    return [...items, { id: `more:${kind}`, kind: 'more', label: labels.more(count), path }]
  }

  const exact: PaletteGroupId | null = data.submissions.some(
    (submission) => normalize(submission.referenceNo) === q,
  )
    ? 'submissions'
    : data.workCenters.some((center) => normalize(center.code) === q)
      ? 'workCenters'
      : null

  return {
    submissions: withMore('submissions', submissions),
    workCenters: withMore('workCenters', workCenters),
    people: withMore('people', people),
    exact,
  }
}

// ---------------------------------------------------------------------------
// The groups, in order
// ---------------------------------------------------------------------------

export interface MergeInput {
  query: string
  actions: PaletteItem[]
  pages: PaletteItem[]
  recent: PaletteItem[]
  server: ServerGroups
}

const GROUP_LABEL: Record<PaletteGroupId, TranslationKey> = {
  recent: 'search.group.recent',
  actions: 'search.group.actions',
  pages: 'search.group.pages',
  submissions: 'search.group.submissions',
  workCenters: 'search.group.workCenters',
  people: 'search.group.people',
}

/**
 * Everything the palette shows for a query, as groups in display order.
 *
 * - **Nothing typed:** what was opened recently, then the actions, then every
 *   page — so the palette doubles as the whole menu, one keystroke away.
 * - **Something typed:** the actions and pages that match come first, because
 *   they are few, answer instantly and are what somebody typing "postavke"
 *   means. Then the server's groups in a fixed order — reports, machines,
 *   people — so a row does not jump between groups as results arrive.
 * - **An exact reference or code:** that group goes to the very top. Typing
 *   a number off a sticker means exactly one thing, and it should be the row
 *   Enter opens.
 *
 * Empty groups are dropped; an empty heading is noise.
 */
export function mergePalette({ query, actions, pages, recent, server }: MergeInput): PaletteGroup[] {
  const group = (id: PaletteGroupId, items: PaletteItem[]): PaletteGroup => ({
    id,
    labelKey: GROUP_LABEL[id],
    items,
  })

  if (!normalize(query)) {
    // A recent page is not offered twice in one list.
    const recentPaths = new Set(recent.map((item) => item.path))

    return [
      group('recent', recent),
      group('actions', actions.filter((item) => !recentPaths.has(item.path))),
      group('pages', pages.filter((item) => !recentPaths.has(item.path))),
    ].filter((entry) => entry.items.length > 0)
  }

  const fromServer = [
    group('submissions', server.submissions),
    group('workCenters', server.workCenters),
    group('people', server.people),
  ]

  const ordered = server.exact
    ? [
        ...fromServer.filter((entry) => entry.id === server.exact),
        group('actions', matchStatic(query, actions, 3)),
        group('pages', matchStatic(query, pages, 5)),
        ...fromServer.filter((entry) => entry.id !== server.exact),
      ]
    : [
        group('actions', matchStatic(query, actions, 3)),
        group('pages', matchStatic(query, pages, 5)),
        ...fromServer,
      ]

  return ordered.filter((entry) => entry.items.length > 0)
}

/** Every row, in the order the arrow keys walk them. */
export function flatten(groups: PaletteGroup[]): PaletteItem[] {
  return groups.flatMap((entry) => entry.items)
}

// ---------------------------------------------------------------------------
// Recently opened
// ---------------------------------------------------------------------------

export const RECENT_LIMIT = 5

/**
 * The list after somebody opens `item`: it goes to the front, an older copy
 * of it goes away, and the list stays short.
 *
 * "Show every match" rows are not kept — they are a query, not a place, and
 * the query that produced them is gone by the time the list is read again.
 */
export function rememberItem(list: PaletteItem[], item: PaletteItem): PaletteItem[] {
  if (item.kind === 'more') return list

  const kept: PaletteItem = {
    id: item.id,
    kind: item.kind,
    label: item.label,
    hint: item.hint,
    badge: item.badge,
    path: item.path,
  }

  return [kept, ...list.filter((entry) => entry.path !== item.path)].slice(0, RECENT_LIMIT)
}

/** A stored list read back, with anything malformed dropped rather than trusted. */
export function parseRecent(raw: string | null): PaletteItem[] {
  if (!raw) return []

  try {
    const parsed: unknown = JSON.parse(raw)

    if (!Array.isArray(parsed)) return []

    return parsed
      .filter(
        (entry): entry is PaletteItem =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as PaletteItem).id === 'string' &&
          typeof (entry as PaletteItem).label === 'string' &&
          typeof (entry as PaletteItem).path === 'string' &&
          // Only same-site paths: this is read back out of storage and
          // navigated to, so anything else is refused.
          (entry as PaletteItem).path.startsWith('/') &&
          !(entry as PaletteItem).path.startsWith('//'),
      )
      .slice(0, RECENT_LIMIT)
  } catch {
    return []
  }
}
